import Phaser from "phaser";
import {
  BOTTOM_BAR_HEIGHT,
  GAME_HEIGHT,
  TILE_SIZE,
  TOP_BAR_HEIGHT,
  TOWERS,
  TowerStats,
  getTowerBuildCost,
  getTowerLevelStats
} from "../config";
import { MapData, pixelToTile, tileAt, tileCenter } from "../map/MapData";
import { Economy } from "./Economy";
import { save } from "./Save";
import { sound } from "./Sound";
import { Tower } from "../entities/Tower";
import { Enemy } from "../entities/Enemy";
import { Projectile } from "../entities/Projectile";

export class TowerPlacement {
  private selected: TowerStats | null = null;
  private ghost?: Phaser.GameObjects.Image;
  private rangePreview?: Phaser.GameObjects.Arc;
  private gridOverlay: Phaser.GameObjects.Graphics;
  private hoverOverlay: Phaser.GameObjects.Graphics;
  private tutorialLockedTowerKey: string | null = null;
  private tutorialTile: { c: number; r: number } | null = null;
  private tutorialSelectionBlocked = false;
  private onEsc = () => this.cancel();
  /** tile-key -> Tower placed there (used for click-to-select on existing towers) */
  private occupied = new Map<string, Tower>();

  constructor(
    private scene: Phaser.Scene,
    private map: MapData,
    private economy: Economy,
    private towers: Tower[],
    private enemiesGetter: () => Enemy[],
    private spawnProjectile: (p: Projectile) => void
  ) {
    this.gridOverlay = scene.add.graphics().setDepth(-2).setVisible(false);
    this.hoverOverlay = scene.add.graphics().setDepth(-1).setVisible(false);
    scene.input.on("pointermove", this.onMove, this);
    scene.input.on("pointerdown", this.onDown, this);
    scene.input.keyboard?.on("keydown-ESC", this.onEsc);
  }

  destroy(): void {
    this.scene.input.off("pointermove", this.onMove, this);
    this.scene.input.off("pointerdown", this.onDown, this);
    this.scene.input.keyboard?.off("keydown-ESC", this.onEsc);
    this.ghost?.destroy();
    this.rangePreview?.destroy();
    this.gridOverlay.destroy();
    this.hoverOverlay.destroy();
    this.ghost = undefined;
    this.rangePreview = undefined;
  }

  select(stats: TowerStats | null): boolean {
    if (!stats && this.tutorialSelectionBlocked) return false;
    if (stats && this.tutorialLockedTowerKey && stats.key !== this.tutorialLockedTowerKey) return false;
    if (stats && this.tutorialSelectionBlocked && !this.tutorialLockedTowerKey) return false;
    this.selected = stats;
    this.ghost?.destroy();
    this.rangePreview?.destroy();
    this.ghost = undefined;
    this.rangePreview = undefined;
    this.gridOverlay.clear().setVisible(Boolean(stats));
    this.hoverOverlay.clear().setVisible(Boolean(stats));
    if (stats) {
      // Ghost & preview always reflect the level-1 (newly built) tower.
      const lvl1 = getTowerLevelStats(stats, 1);
      this.ghost = this.scene.add.image(-9999, -9999, `tower-${stats.key}-1`).setAlpha(0.7);
      // Mirror the placed tower's anchor: bottom of the sprite on the tile's
      // bottom edge, at the level-1 size (see Tower.baseSize).
      this.ghost.setOrigin(0.5, 1);
      this.ghost.setDisplaySize(TILE_SIZE * 1.02, TILE_SIZE * 1.02);
      this.rangePreview = this.scene.add.circle(-9999, -9999, lvl1.range, 0xffffff, 0.08)
        .setStrokeStyle(1, 0xffffff, 0.5)
        .setDepth(1);
      this.drawPlacementGrid();
    }
    return true;
  }

  cancel(): void {
    if (this.tutorialSelectionBlocked) return;
    this.select(null);
  }

  setTutorialPlacement(towerKey: string | null, tile: { c: number; r: number } | null, blockSelection: boolean = false): void {
    this.tutorialLockedTowerKey = towerKey;
    this.tutorialTile = tile;
    this.tutorialSelectionBlocked = blockSelection || towerKey !== null;
    if (!towerKey) return;
    const lockedTower = Object.values(TOWERS).find((tower) => tower.key === towerKey) ?? null;
    if (lockedTower) this.select(lockedTower);
  }

  /** True while a tower is selected for placement (build ghost active). */
  get isPlacing(): boolean { return this.selected !== null; }

  private key(c: number, r: number): string { return `${c},${r}`; }

  private canPlace(c: number, r: number): boolean {
    if (this.tutorialSelectionBlocked && !this.tutorialTile) return false;
    if (this.tutorialTile && (this.tutorialTile.c !== c || this.tutorialTile.r !== r)) return false;
    if (c < 0 || c >= this.map.cols || r < 0 || r >= this.map.rows) return false;
    if (this.occupied.has(this.key(c, r))) return false;
    // A saved editor mask is authoritative: 1 allows placement and 0 blocks it.
    if (this.map.placementMask) return this.map.placementMask[r]?.[c] === true;
    const kind = tileAt(this.map, c, r);
    if (kind !== "grass") return false;
    if (this.map.blocked?.has(this.key(c, r))) return false;
    return true;
  }

  private isOverUi(y: number): boolean {
    return y < TOP_BAR_HEIGHT || y > GAME_HEIGHT - BOTTOM_BAR_HEIGHT;
  }

  /**
   * Only shown while a tower is selected: buildable cells get a restrained
   * green outline, while road/obstacle cells are marked in warm red. This
   * exposes the placement rules without permanently covering the map art.
   */
  private drawPlacementGrid(): void {
    this.gridOverlay.clear();
    if (!this.selected) return;

    for (let r = 0; r < this.map.rows; r++) {
      for (let c = 0; c < this.map.cols; c++) {
        const x = this.map.offsetX + c * this.map.tileSize;
        const y = this.map.offsetY + r * this.map.tileSize;
        const valid = this.canPlace(c, r);
        const kind = tileAt(this.map, c, r);
        if (valid) {
          this.gridOverlay.lineStyle(1, 0xb8e6a2, 0.34);
          this.gridOverlay.strokeRect(x + 2, y + 2, this.map.tileSize - 4, this.map.tileSize - 4);
        } else if (kind === "path" || kind === "castle-gate") {
          this.gridOverlay.fillStyle(0x9f4436, 0.08);
          this.gridOverlay.fillRect(x + 2, y + 2, this.map.tileSize - 4, this.map.tileSize - 4);
          this.gridOverlay.lineStyle(1, 0xd27b68, 0.28);
          this.gridOverlay.strokeRect(x + 2, y + 2, this.map.tileSize - 4, this.map.tileSize - 4);
        } else {
          this.gridOverlay.lineStyle(1, 0xd7c8aa, 0.12);
          this.gridOverlay.strokeRect(x + 2, y + 2, this.map.tileSize - 4, this.map.tileSize - 4);
        }
      }
    }
  }

  private drawHover(c: number, r: number, ok: boolean): void {
    this.hoverOverlay.clear();
    if (c < 0 || r < 0 || c >= this.map.cols || r >= this.map.rows) return;
    const x = this.map.offsetX + c * this.map.tileSize;
    const y = this.map.offsetY + r * this.map.tileSize;
    const color = ok ? 0x78c85d : 0xd25c4a;
    this.hoverOverlay.fillStyle(color, 0.20);
    this.hoverOverlay.fillRect(x + 3, y + 3, this.map.tileSize - 6, this.map.tileSize - 6);
    this.hoverOverlay.lineStyle(2, color, 0.92);
    this.hoverOverlay.strokeRect(x + 2, y + 2, this.map.tileSize - 4, this.map.tileSize - 4);
  }

  private onMove = (pointer: Phaser.Input.Pointer): void => {
    if (!this.selected || !this.ghost || !this.rangePreview) return;
    if (this.isOverUi(pointer.worldY)) {
      this.ghost.setPosition(-9999, -9999);
      this.rangePreview.setPosition(-9999, -9999);
      this.hoverOverlay.clear();
      return;
    }
    const { c, r } = pixelToTile(this.map, pointer.worldX, pointer.worldY);
    const center = tileCenter(this.map, c, r);
    this.ghost.setPosition(center.x, center.y + TILE_SIZE / 2);
    this.ghost.setDepth(center.y + TILE_SIZE / 2);
    this.rangePreview.setPosition(center.x, center.y);
    const cost = Math.round(getTowerBuildCost(this.selected) * this.economy.modifiers.buildCostMult);
    const ok = this.canPlace(c, r) && this.economy.canAfford(cost);
    this.ghost.setTint(ok ? 0xaaffaa : 0xff8888);
    this.drawHover(c, r, ok);
  };

  private onDown = (pointer: Phaser.Input.Pointer): void => {
    if (this.isOverUi(pointer.worldY)) return;
    const { c, r } = pixelToTile(this.map, pointer.worldX, pointer.worldY);

    // If the player clicked an existing tower, defer to its own pointerdown
    // (which emits "tower:selected"). Don't try to place anything.
    if (this.occupied.has(this.key(c, r))) return;

    if (!this.selected) return;
    if (!this.canPlace(c, r)) {
      this.scene.events.emit("tower:place-failed", { reason: "blocked" });
      return;
    }
    const cost = Math.round(getTowerBuildCost(this.selected) * this.economy.modifiers.buildCostMult);
    if (!this.economy.spend(cost)) {
      this.scene.events.emit("tower:place-failed", { reason: "gold" });
      return;
    }

    const center = tileCenter(this.map, c, r);
    const tower = new Tower(
      this.scene, center.x, center.y,
      this.selected, this.enemiesGetter, this.spawnProjectile,
      () => this.towers, cost
    );
    this.towers.push(tower);
    this.occupied.set(this.key(c, r), tower);
    save.recordTowerBuilt(this.selected.key);
    sound.playUi("buy");
    this.scene.events.emit("tower:placed", { tower, c, r });
    this.drawPlacementGrid();

    // keep selected so player can place multiple; ESC cancels
  };

  /** Remove a placed tower, free its tile, and refund its recorded investment. */
  sell(tower: Tower): number | null {
    const occupiedEntry = [...this.occupied.entries()].find(([, placed]) => placed === tower);
    if (!occupiedEntry) return null;

    this.occupied.delete(occupiedEntry[0]);
    const index = this.towers.indexOf(tower);
    if (index >= 0) this.towers.splice(index, 1);

    const refund = this.economy.sellRefund(tower.totalInvested);
    tower.destroy();
    this.scene.events.emit("tower:sold", { tower, refund });
    this.drawPlacementGrid();
    return refund;
  }
}
